import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Toaster } from '../common';
import { startDirectMessage } from '../../services/chatServices';
import type { Match } from '../../types/match';
import toast from 'react-hot-toast';
import { ChatLines } from 'iconoir-react';

interface InitiateAIChatButtonProps {
  match: Match;
  variant: 'horizontal' | 'vertical';
  /** Apr-25: when true, label changes from "Start Conversation" to
   *  "Open Conversation" — startDirectMessage is idempotent and returns the
   *  existing channel, so the same button can re-enter an active thread. */
  alreadyInitiated?: boolean;
}

const InitiateAIChatButton = ({
  match,
  variant,
  alreadyInitiated = false,
}: InitiateAIChatButtonProps) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const handleStartConversation = async () => {
    setIsLoading(true);
    try {
      const conversationId = await startDirectMessage(match.other_user.id, match.id);
      navigate(`/chat?channel=${conversationId}`);
    } catch (error: any) {
      toast.custom(t => (
        <Toaster
          message={error?.message || 'Could not open conversation'}
          type='error'
          id={t.id}
        />
      ));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      // Apr-25: data-tour-id moved here from the deleted MatchCard
      // duplicate Button. The match-detail tour's last step targets this
      // selector ("Ready to reach out?" coach mark).
      data-tour-id='match-detail-start'
      size='sm'
      color='dark'
      className={`my-1 ${variant === 'horizontal' ? 'w-full md:w-fit ml-auto' : 'w-full'}`}
      onClick={handleStartConversation}
      isLoading={isLoading}
    >
      <ChatLines className='w-5 h-5' />{' '}
      {alreadyInitiated ? 'Open Conversation' : 'Start Conversation'}
    </Button>
  );
};

export default InitiateAIChatButton;
